import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { localeData } from '../../locales/data';
import { columnsNumberTypes } from '../../shared/data';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { FilterBlurEvent, FilterValues } from './tableFilter.types';

interface FilterInputProps {
  value: string;
  filters: FilterValues;
  id: string;
  parent?: string;
  type: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: FilterBlurEvent) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const WAIT_INTERVAL = 1000;

export const FilterInput = ({ value, filters, id, parent, type, onChange, onBlur, onFocus }: FilterInputProps) => {
  const [inputValue, setInputValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const triggerChange = (target: HTMLInputElement) => {
    onBlur({ relatedTarget: null, target });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isNumberType = type === 'integer' || type === 'amount';
    if (!(isNumberType && event.target.value.match(/[.,]/))) {
      clearTimer();
      onChange(event);
      const eventTarget = event.target;
      setInputValue(event.target.value);

      timerRef.current = setTimeout(() => triggerChange(eventTarget), WAIT_INTERVAL);
    }
  };

  const handlerOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    clearTimer();
    onBlur({ relatedTarget: event.relatedTarget as Element | null, target: event.currentTarget });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      clearTimer();
      onBlur({ relatedTarget: null, target: event.currentTarget });
    }
  };

  const filterKey = parent ? `${parent}.${id}` : id;
  const isMulti = Object.prototype.hasOwnProperty.call(filters, filterKey) && /&+/.test(value);
  const placeholder = columnsNumberTypes.includes(type) ? 'e.g > 1' : 'Search';

  return (
    <div className={isMulti ? 'multi-filter' : ''}>
      <input
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handlerOnBlur}
        onFocus={onFocus}
        value={inputValue}
        onKeyPress={handleKeyPress}
        className="table__input"
      />

      {isMulti && <FormattedMessageHelper tagName="span" id="table.getInput.1" defaultMessage={localeData.en['table.getInput.1']} />}
    </div>
  );
};
